import { useControlledSearchInput } from '@deltasierra/react-hooks';
import React, { CSSProperties, memo, useEffect, useMemo, useRef } from 'react';

import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList, areEqual } from 'react-window';
import { DSTextField } from '../../../mui';
import { AssetCheckboxClientGroupCheckbox } from '../AssetCheckboxClientGroupCheckbox';
import { AssetShareTargetCheckbox } from '../AssetShareTargetCheckbox';
import { SvgLoading } from '../../../Generic';
import { PickableClientOrLocation, PickableClientOrLocationGroup } from './types';

type LocationPickerItemProps = {
    data: {
        clientsOrLocations: ClientOrLocation[];
        disabled: ReadonlyArray<string>;
        onChange: (selected: string[]) => void;
        selected: ReadonlyArray<string>;
    };
    index: number;
    style: CSSProperties;
};

const LocationPickerItem = memo(({ data, index, style }: LocationPickerItemProps): JSX.Element => {
    const { clientsOrLocations, disabled, onChange, selected } = data;

    const item = clientsOrLocations[index];

    return (
        <span style={style}>
            {item.type === 'client' ? (
                <AssetCheckboxClientGroupCheckbox
                    areAllLocationsChecked={item.client.areAllLocationsChecked}
                    areAllLocationsDisabled={item.client.areAllLocationsDisabled}
                    client={item.client}
                    disabled={disabled}
                    isAtLeastOneLocationChecked={item.client.isAtLeastOneLocationChecked}
                    selected={selected}
                    onChange={onChange}
                />
            ) : (
                <AssetShareTargetCheckbox
                    disabled={disabled}
                    item={item.location}
                    selected={selected}
                    style={{ paddingLeft: '8px' }}
                    onChange={onChange}
                />
            )}
        </span>
    );
}, areEqual);
LocationPickerItem.displayName = 'LocationPickerItem';

type ClientOrLocation =
    | {
          type: 'client';
          client: PickableClientOrLocationGroup & {
              areAllLocationsChecked: boolean;
              areAllLocationsDisabled: boolean;
              isAtLeastOneLocationChecked: boolean;
          };
      }
    | { type: 'location'; location: PickableClientOrLocation };

export type LocationPickerProps = {
    clients: ReadonlyArray<PickableClientOrLocationGroup>;
    disabled?: ReadonlyArray<string>;
    loading?: boolean;
    onChange: (selected: string[]) => void;
    onSearchTermChange?: (newTerm?: string) => void;
    selected: ReadonlyArray<string>;
};

// eslint-disable-next-line max-lines-per-function
export const LocationPicker: React.FC<LocationPickerProps> = ({
    clients,
    disabled = [],
    loading,
    onChange,
    onSearchTermChange,
    selected,
}) => {
    const listRef = useRef<VariableSizeList>(null);
    const { inputProps, searchTerm } = useControlledSearchInput({
        initialValue: '',
        interval: 250,
        onSearchTermValueChange: onSearchTermChange,
    });

    /*
     * Const selectedLocations = React.useMemo(
     *     () =>
     *         Clients
     *             .reduce<PickableClientOrLocation[]>((carry, current) => [...carry, ...current.items], [])
     *             .filter(item => selected.indexOf(item.id) >= 0),
     *     [clients, selected],
     * );
     */

    const filteredItems = useMemo(
        () =>
            clients
                .reduce<PickableClientOrLocationGroup[]>((carry, current) => {
                    if (current.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())) {
                        return [...carry, current];
                    } else {
                        const filtered = current.items.filter(item =>
                            item.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
                        );
                        return [
                            ...carry,
                            {
                                ...current,
                                items: filtered,
                            },
                        ];
                    }
                }, [])
                .filter(client => client.items.length > 0),
        [clients, searchTerm],
    );

    const clientsOrLocations = useMemo(
        (): ClientOrLocation[] =>
            filteredItems.flatMap(client => {
                const selectedItems = client.items.filter(item => selected.includes(item.id));
                const areAllLocationsChecked = client.items.length === selectedItems.length;
                const isAtLeastOneLocationChecked = selectedItems.length > 0;
                const areAllLocationsDisabled = client.items.every(item => disabled.includes(item.id));

                return [
                    {
                        client: {
                            ...client,
                            areAllLocationsChecked,
                            areAllLocationsDisabled,
                            isAtLeastOneLocationChecked,
                        },
                        type: 'client',
                    } satisfies ClientOrLocation,
                    ...client.items.map((location): ClientOrLocation => ({ location, type: 'location' })),
                ];
            }),
        [disabled, filteredItems, selected],
    );

    const itemData = useMemo(
        () => ({
            clientsOrLocations,
            disabled,
            onChange,
            selected,
        }),
        [clientsOrLocations, disabled, onChange, selected],
    );

    useEffect(() => {
        if (clientsOrLocations.length > 0 && listRef.current) {
            listRef.current.resetAfterIndex(0);
        }
    }, [clientsOrLocations]);

    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%', overflow: 'hidden' }}
        >
            <DSTextField fullWidth label="Filter" variant="outlined" {...inputProps} />
            <div style={{ flex: 1 }}>
                <AutoSizer>
                    {({ height, width }) => (
                        <VariableSizeList
                            estimatedItemSize={45}
                            height={height}
                            itemCount={clientsOrLocations.length}
                            itemData={itemData}
                            itemKey={(index, data) => {
                                const item = data.clientsOrLocations[index];
                                return item.type === 'client'
                                    ? `client.${item.client.id}`
                                    : `location.${item.location.id}`;
                            }}
                            itemSize={index => (clientsOrLocations[index].type === 'client' ? 30 : 45)}
                            overscanCount={20}
                            ref={listRef}
                            width={width}
                        >
                            {LocationPickerItem}
                        </VariableSizeList>
                    )}
                </AutoSizer>
            </div>
            {loading && <SvgLoading />}
        </div>
    );
};
LocationPicker.displayName = 'LocationPicker';
